import React, { useState, useEffect } from 'react';
import { Input } from './input';
import { cn } from '../../lib/utils';

/**
 * Input component that formats numbers with space as thousands separator
 * Example: 2000000 displays as "2 000 000"
 */
export const FormattedNumberInput = ({
  value,
  onChange,
  className,
  placeholder,
  suffix = '',
  allowNegative = false,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Format number with spaces as thousands separator
  const formatWithSpaces = (num) => {
    if (num === null || num === undefined || num === '') return '';
    const numStr = String(num).replace(/\s/g, '');
    if (numStr === '' || numStr === '-') return numStr;
    
    const isNegative = numStr.startsWith('-');
    const absoluteValue = isNegative ? numStr.slice(1) : numStr;
    
    // Split by decimal point if exists
    const parts = absoluteValue.split('.');
    const integerPart = parts[0] || '';
    const decimalPart = parts[1];
    
    // Add spaces for thousands
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    let result = isNegative ? '-' + formatted : formatted;
    if (decimalPart !== undefined) {
      result += '.' + decimalPart;
    }
    
    return result;
  };

  // Parse formatted string back to number
  const parseToNumber = (str) => {
    if (!str || str === '' || str === '-') return null;
    const cleaned = str.replace(/\s/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  // Update display when external value changes
  useEffect(() => {
    setDisplayValue(formatWithSpaces(value));
  }, [value]);

  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Allow only digits, spaces, minus (if allowed), and decimal point
    const regex = allowNegative 
      ? /^-?[\d\s]*\.?\d*$/ 
      : /^[\d\s]*\.?\d*$/;
    
    // Remove all spaces first to check the pattern
    const cleanedForCheck = inputValue.replace(/\s/g, '');
    
    if (!regex.test(cleanedForCheck) && cleanedForCheck !== '') {
      return; // Invalid input, ignore
    }
    
    // Format the display value
    const formatted = formatWithSpaces(cleanedForCheck);
    setDisplayValue(formatted);
    
    // Call onChange with the numeric value
    const numericValue = parseToNumber(cleanedForCheck);
    if (onChange) {
      onChange(numericValue);
    }
  };

  const handleBlur = () => {
    // Re-format on blur to ensure consistent display
    setDisplayValue(formatWithSpaces(value));
  };

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn("font-mono", suffix && "pr-8", className)}
        placeholder={placeholder}
        {...props}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
};

export default FormattedNumberInput;
